import { Component, ElementRef, Input, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { InfiniteScrollCustomEvent, IonContent } from '@ionic/angular';
import { AlcmeonContextService } from 'src/app/core/alcmeon-context/alcmeon-context.service';
import { API_URL_PREFIX, BasicService } from 'src/app/shared/services/basic.service';
import { ModalDismissRoleEnum } from 'src/app/task-process/answer-editor/send-and-popover/modal-dismiss-role.enum';
import { TaskProcessContext } from 'src/app/task-process/shared/task-process-context.class';
import { environment } from 'src/environments/environment';

import { Favorite } from '../../models';
import { Tag } from '../../models/misc.model';
import { SelectTagPopoverComponent } from '../select-tags/select-tag-popover/select-tag-popover.component';

@Component({
  selector: 'app-favorites-modal',
  templateUrl: './favorites-modal.component.html',
  styleUrls: ['./favorites-modal.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class FavoritesModalComponent implements OnInit {
  @Input() favorites: Favorite[]; // Contains all available favorites at any time
  @Input() taskProcessContext: TaskProcessContext;
  filteredFavorites: Favorite[];
  paginatedFilteredFavorites: Favorite[]; // Displayed in the UI

  pictureURL = `${API_URL_PREFIX}/${this.alcmeonContext.companyId}/pictures/`;
  videoURL = `${API_URL_PREFIX}/${this.alcmeonContext.companyId}/videos/`;

  bundledWithAngularJSApp = environment.bundledWithAngularJSApp;
  languageTranslation = '';
  searchText: string;
  selectedLanguage: Tag;

  @ViewChild(IonContent, { read: ElementRef }) ionContent: ElementRef;

  constructor(private basicService: BasicService, private alcmeonContext: AlcmeonContextService) {}

  ngOnInit() {
    this.favorites?.forEach(fav => (fav.videoPlaceholderClicked = false)); // Initially false to display black box in place of video

    this.filteredFavorites = this.favorites ? [...this.favorites] : []; // Create a copy of the original array

    this.paginatedFilteredFavorites = this.filteredFavorites.slice(0, 10); // Feed the first 10 favorites to be displayed in the UI

    // no taskProcessContext on publications
    if (this.taskProcessContext) {
      const languageTag = this.taskProcessContext.tagsContext.allTagGroups.find(tagGroup => tagGroup.isLanguage);
      this.languageTranslation = languageTag.name;

      this.selectedLanguage = languageTag.tags.find(tag => {
        return tag.id === languageTag.selectedTagId && tag.id !== languageTag.noneTagId;
      });

      this.filterFavorites();
    }
  }

  dismissModal() {
    this.basicService.dismissModal();
  }

  onFavClick(favorite: Favorite) {
    this.basicService.dismissModal(favorite);
  }

  // Filter favorites title | text according to search input
  onSearchInputChange($event) {
    this.favorites?.forEach(fav => (fav.videoPlaceholderClicked = false)); // Reset the video placeholders
    this.searchText = $event.target.value.toLowerCase();
    this.filterFavorites();
  }

  loadMoreFavorites(event: Event) {
    // Load next 10 favorites
    this.paginatedFilteredFavorites.push(
      ...this.filteredFavorites.slice(
        this.paginatedFilteredFavorites.length,
        this.paginatedFilteredFavorites.length + 10
      )
    );
    (event as InfiniteScrollCustomEvent).target.complete();
  }

  async selectLanguage(event: Event) {
    const componentProps = {
      group: this.taskProcessContext.tagsContext.allTagGroups[1],
      tagsContext: this.taskProcessContext.tagsContext,
      hideSelection: true,
    };

    const modalResult = await this.basicService.popoverWithResult(
      event,
      SelectTagPopoverComponent,
      componentProps,
      'alc-select-tag-in-popover'
    );

    if (modalResult.role === ModalDismissRoleEnum.OK) {
      this.selectedLanguage = modalResult.data;
      this.filterFavorites();
    }
  }

  clearLang(event: Event) {
    event.stopPropagation();
    this.selectedLanguage = null;
    this.filterFavorites();
  }

  favoriteTrackBy(index: number, favorite: Favorite) {
    return favorite.id;
  }

  private filterFavorites() {
    this.filteredFavorites = this.favorites.filter(favorite => {
      const matchSearch =
        !this.searchText ||
        favorite.title.toLowerCase().includes(this.searchText) ||
        (favorite.editorModel?.text || '').toLowerCase().includes(this.searchText);

      const matchLang =
        !this.selectedLanguage ||
        favorite.tagsById[favorite.languageTagId]?.length === 0 || // always include "all languages"
        favorite.tagsById[favorite.languageTagId]?.includes(this.selectedLanguage.id);

      return matchSearch && matchLang;
    });

    // Combined with setTimeout above, prevent a bug from ion-infinite-scroll :
    // when reached the end of scroll, and the list of fav changes, the scroll detection does not work anymore
    this.ionContent?.nativeElement.scrollToTop();

    setTimeout(() => {
      this.paginatedFilteredFavorites = this.filteredFavorites.slice(0, 10);
    });
  }
}
